import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { isNil, toLower } from 'lodash';
import find from 'lodash/find';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Link,
  Snackbar,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {
  verifyCommunity,
  verifyRentCafe
} from '../../../Redux/Actions/SelfServe';
import { verifyCommunityError } from '../../../Redux/Reducer/SelfServe';

const fieldLabelSx = {
  fontFamily: 'Inter, sans-serif',
  fontWeight: 500,
  fontSize: '16px',
  lineHeight: '19px',
  color: 'text.secondary',
  mb: '6px'
};

const CommunityInformation = ({
  apartmentId,
  header = 'CRM Configuration',
  info,
  infoPopUPHandler,
  apartmentData,
  crmSettings
}) => {
  const dispatch = useDispatch();
  const selfServe = useSelector(state => state.SelfServe);
  const integrationSettings = selfServe?.userData?.data?.integrationSettings;
  const pmsValue = integrationSettings && integrationSettings?.pms;
  const [notification, setNotification] = useState('');
  const [onBoard, setOnBoard] = useState(false);
  const enableKnockCrm = selfServe?.userData?.data?.enableKnockCRM;
  const { individualCommunity } = selfServe;
  const [inputValue, setInputValue] = useState('');
  const [copied, setCopied] = useState(false);
  const copyTimeoutRef = useRef(null);
  const knockErrorDetails = selfServe?.verifyCommunityKnockDetails;

  const copyKnockDetails = () => {
    navigator.clipboard.writeText(JSON.stringify(knockErrorDetails, null, 2));
    setCopied(true);
    clearTimeout(copyTimeoutRef.current);
    copyTimeoutRef.current = setTimeout(() => setCopied(false), 2000);
  };

  const isYardiNoCrm = isNil(crmSettings) && toLower(pmsValue) === 'yardi';
  const isRealpageKnockCRM =
    toLower(pmsValue)?.replace(/\s/g, '') === 'realpage' && enableKnockCrm;
  const missingCompanyId =
    isRealpageKnockCRM && !integrationSettings?.companyId;
  const hasIntegration = enableKnockCrm || isYardiNoCrm;
  // Skip step 0 (community name, timezone, tour details) — go straight to integration step
  const activeStep = isYardiNoCrm ? 2 : 1;

  const closeNotification = () => {
    setNotification('');
  };
  const showNotification = (_, error = false) => {
    if (error) {
      setOnBoard(false);
      return;
    }

    if (selfServe?.verifyRentCafeData?.success) {
      setOnBoard(true);
    }
    if (selfServe?.verifyRentCafeData?.message)
      setNotification((selfServe?.verifyRentCafeData?.message ?? '') + '!');
  };
  useEffect(() => {
    const { leasingAgents = {} } =
      find(apartmentData, { _id: apartmentId }) ?? {};
    if (leasingAgents?.Kelsey) {
      setInputValue(leasingAgents?.Kelsey?.username);
    }
  }, [apartmentId]);
  const verifyId = () => {
    if (isYardiNoCrm) {
      const payload = {
        tenantId: find(apartmentData, { _id: apartmentId }).tenantId,
        PropertyCode: individualCommunity[activeStep]?.[2]?.value,
        apartmentId: apartmentId,
        CompanyCode: individualCommunity[activeStep]?.[0]?.value,
        rentcafeApiV2Token: individualCommunity[activeStep]?.[1]?.value
      };
      dispatch(verifyRentCafe({ payload, showNotification }));
    } else {
      const payload = {
        communityId: individualCommunity[activeStep]?.[0]?.value,
        aptId: apartmentId,
        tenantId: find(apartmentData, { _id: apartmentId }).tenantId
      };
      dispatch(verifyCommunity({ payload, showNotification }));
    }
  };

  const onInputChange = e => {
    const { value, name } = e.target;
    dispatch(verifyCommunityError({ name, value, step: activeStep }));
  };
  const saveCommunity = () => {
    setNotification('Community Saved');
    closePop();
  };

  const closePop = () => {
    infoPopUPHandler(false);
  };

  useEffect(() => {
    if (enableKnockCrm) {
      // Knock: onBoard when all 3 fields in step 1 are filled
      if (
        pmsValue?.toLowerCase() === 'realpage' &&
        (individualCommunity?.[1]?.filter(({ value }) => value)).length === 3
      ) {
        setOnBoard(true);
      } else if (individualCommunity?.[1]?.[0]?.verify) {
        setOnBoard(true);
      }
    } else if (isYardiNoCrm) {
      // Yardi: onBoard when RentCafe is verified
      if (selfServe?.verifyRentCafeData?.success) {
        setOnBoard(true);
      }
    }
  }, [selfServe?.individualCommunity, selfServe?.verifyRentCafeData]);

  const renderVerifyAdornment = (error, verify) => {
    if (error) return <ErrorOutlineIcon sx={{ color: '#FF4D00' }} />;
    if (verify && !error) return <CheckCircleIcon sx={{ color: '#50BE77' }} />;
    return null;
  };

  const renderInput = individualCommunity?.[activeStep]?.map(
    ({ label, endAdornment, type, name, value, error, verify, disable }) => {
      return (
        <React.Fragment key={label}>
          {type && type === 'select' ? (
            <Box sx={{ mb: '16px' }}>
              <Typography sx={fieldLabelSx}>
                {label}
                {!value?.value && (
                  <Typography
                    component="span"
                    sx={{ color: 'error.main', ml: '2px' }}
                  >
                    *
                  </Typography>
                )}
              </Typography>
              {pmsValue === 'yardi' && !enableKnockCrm && (
                <TextField
                  size="small"
                  fullWidth
                  disabled
                  value={inputValue || ''}
                />
              )}
            </Box>
          ) : (
            <Box sx={{ mb: '16px' }}>
              <Typography sx={fieldLabelSx}>{label}</Typography>
              <TextField
                size="small"
                fullWidth
                disabled={disable}
                name={name}
                value={value || ''}
                onChange={onInputChange}
                InputProps={{
                  endAdornment:
                    !disable && (error || verify || endAdornment) ? (
                      <InputAdornment
                        position="end"
                        sx={{ cursor: endAdornment ? 'pointer' : 'default' }}
                      >
                        {renderVerifyAdornment(error, verify) ||
                          (endAdornment && (
                            <Link
                              component="button"
                              underline="hover"
                              onClick={() => verifyId()}
                              sx={{ fontSize: '14px', fontWeight: 500 }}
                            >
                              Verify
                            </Link>
                          ))}
                      </InputAdornment>
                    ) : null
                }}
              />
            </Box>
          )}
        </React.Fragment>
      );
    }
  );

  return (
    <>
      <Dialog open={info} onClose={closePop} maxWidth="sm" fullWidth>
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <Typography
              variant="h6"
              sx={{ fontWeight: 500, color: 'text.secondary' }}
            >
              {header}
            </Typography>
            {isYardiNoCrm && (
              <Link
                component="button"
                underline="hover"
                onClick={() =>
                  window.open(
                    import.meta.env.VITE_RENTCAFE_RETOOL_DIAGNOSE_URL ?? '',
                    'WindowName',
                    'noopener'
                  )
                }
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '4px',
                  fontSize: '14px'
                }}
              >
                Diagnose
                <OpenInNewIcon sx={{ fontSize: '16px' }} />
              </Link>
            )}
          </Box>
          <IconButton onClick={closePop} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          {hasIntegration ? (
            <>
              {missingCompanyId && (
                <Alert severity="warning" sx={{ mb: '16px' }}>
                  Company Id is missing in PMS configuration. Community
                  verification will fail until both PMC Id and Company Id are
                  set.
                </Alert>
              )}
              {renderInput}
              <Typography
                variant="body2"
                sx={{ color: 'text.secondary', mt: '16px' }}
              >
                To update office hours or community website, visit the community
                settings page at{' '}
                <Link
                  href="https://client.getzuma.com"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  client.getzuma.com
                </Link>
                .
              </Typography>
            </>
          ) : (
            <Typography sx={{ color: 'text.secondary', py: '16px' }}>
              No CRM configuration for this community.
            </Typography>
          )}
        </DialogContent>

        {hasIntegration && (
          <DialogActions
            sx={{
              justifyContent: 'space-between',
              px: '24px',
              py: '16px'
            }}
          >
            <Box>
              {knockErrorDetails && (
                <Tooltip
                  title={
                    copied
                      ? 'Copied!'
                      : 'Copy error details for Knock support ticket'
                  }
                >
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<ContentCopyIcon fontSize="small" />}
                    onClick={copyKnockDetails}
                    color={copied ? 'success' : 'error'}
                  >
                    {copied ? 'Copied!' : 'Copy Error Details'}
                  </Button>
                </Tooltip>
              )}
            </Box>
            <Tooltip
              title={!onBoard ? 'One or more mandatory fields are missing' : ''}
            >
              <span>
                <Button
                  variant="contained"
                  disabled={!onBoard}
                  onClick={saveCommunity}
                >
                  Save
                </Button>
              </span>
            </Tooltip>
          </DialogActions>
        )}
      </Dialog>
      <Snackbar
        open={!!notification}
        autoHideDuration={4000}
        onClose={closeNotification}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={closeNotification} severity="success">
          {notification}
        </Alert>
      </Snackbar>
    </>
  );
};

export default CommunityInformation;
